import React, { useState, useEffect } from 'react'
import { Modal, Typography, ColorCircularProgress } from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'constants/index'
import { useDispatch } from 'react-redux'
import { getClientsSharedByMe, getClientsSharedWithMe } from '../../store/actions/connections'

const { WHITE } = colors

const useStyles = makeStyles({
  modal: {
    width: 342,
    position: 'relative',
    padding: '24px 16px',
    backgroundColor: WHITE,
    boxShadow: '2px 4px 24px 0px rgba(0, 0, 0, 0.15)',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  title: {
    margin: '0 0 24px 0',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: '24px',
    color: '#2B4157',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  projectItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingBottom: 4,
  },
  divider: {
    height: 1,
    backgroundColor: '#DDE2EB',
    margin: '4px 0',
  },
  row: {
    display: 'flex',
    gap: 8,
    fontSize: 13,
    lineHeight: '19px',
    color: '#011A33',
  },
  label: {
    minWidth: 74,
    flexShrink: 0,
    color: '#596C80',
  },
  value: {
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
})

const SharedClientProjectsModal = ({ isOpen, onClose, clientUuid, clientName, type = 'shared_by_me', proUuid }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [projects, setProjects] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen && clientUuid) {
      setIsLoading(true)
      if (type === 'shared_by_me') {
        dispatch(getClientsSharedByMe(clientUuid))
          .then(data => {
            setProjects(Array.isArray(data) ? data : [])
            setIsLoading(false)
          })
          .catch(() => {
            setIsLoading(false)
          })
      } else {
        dispatch(getClientsSharedWithMe(clientUuid, proUuid))
          .then(data => {
            setProjects(Array.isArray(data) ? data : [])
            setIsLoading(false)
          })
          .catch(() => {
            setIsLoading(false)
          })
      }
    } else if (!isOpen) {
      setProjects([])
    }
  }, [isOpen, clientUuid, type, proUuid, dispatch])

  const formatSharedWith = sharedWith => {
    if (!Array.isArray(sharedWith) || sharedWith.length === 0) {
      return ''
    }
    // shared_with can be array of strings or array of objects with full_name
    return sharedWith
      .map(item => (typeof item === 'string' ? item : item.full_name))
      .filter(Boolean)
      .join(', ')
  }

  const handleClose = (event, reason) => {
    if (event) {
      event.stopPropagation()
    }
    onClose(event)
  }

  return (
    <Modal open={isOpen} handleClose={handleClose} maxWidth={false}>
      {isLoading ? (
        <ColorCircularProgress />
      ) : (
        <div className={classes.modal} onClick={e => e.stopPropagation()}>
          <Typography variant="h3" className={classes.title}>
            Shared projects for client {clientName || ''}
          </Typography>
          <div className={classes.list}>
            {projects.map((project, index) => (
              <React.Fragment key={project.uuid || index}>
                <div className={classes.projectItem}>
                  <div className={classes.row}>
                    <span className={classes.label}>Project:</span>
                    <span className={classes.value}>{project.name}</span>
                  </div>
                  {type === 'shared_with_me' && project.owner_full_name && (
                    <div className={classes.row}>
                      <span className={classes.label}>Owner:</span>
                      <span className={classes.value}>{project.owner_full_name}</span>
                    </div>
                  )}
                  <div className={classes.row}>
                    <span className={classes.label}>Shared with:</span>
                    <span className={classes.value}>{formatSharedWith(project.shared_with)}</span>
                  </div>
                </div>
                {index < projects.length - 1 && <div className={classes.divider} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </Modal>
  )
}

export default SharedClientProjectsModal
